import type { PrinterProfile, Warning } from '@tsagko/label-contracts'
import type { ZplCommand } from './zpl-command'
import { mmToDots } from './internal/units'
import type { RenderPlan } from './render-plan'

/**
 * Translate a `RenderPlan[]` into a `ZplCommand[]`.
 *
 * Per-variant rules:
 *   - text with `rotation === 'N'` AND `alignment === 'L'` AND
 *     `maxLines === 1` → one `text` command. Skipping the `^FB` prefix
 *     keeps wire output byte-identical to the pre-refactor emitter for
 *     default-aligned single-line text.
 *   - text otherwise → a `field-block` command followed by a `text`
 *     command. ZPL's parser scopes `^FB` to the next `^FS`-terminated
 *     field, so the pair is correctly interpreted at print time. Rotated
 *     text always takes this arm — `^FO` is only a well-defined corner of
 *     the rotated block when a `^FB` is present (see `textPlanGeometry`).
 *   - separator → one `graphic-box` command.
 *   - image with `buffer === null` → no command (silent skip).
 *   - image with buffer → one `image` command.
 *   - barcode → one `barcode` command (routes to the symbology-specific
 *     arm in `serialize.ts`).
 *
 * Pure: no I/O, no globals. Warnings come from upstream `buildRenderPlan`;
 * this function does not produce warnings of its own, but returns the
 * shape `{ commands, warnings }` for API parity with `buildCommands`.
 */
export function commandsFromPlans(
  plans: RenderPlan[],
  profile: PrinterProfile
): { commands: ZplCommand[]; warnings: Warning[] } {
  const commands: ZplCommand[] = []
  // Tracks the box of the most recently emitted region black-fill so plans
  // sharing one region (barcode + its `:hri` line) fill it exactly once —
  // see `emitBlackFill` for the ^FR/XOR rationale.
  const fillState: { lastFillKey: string | null } = { lastFillKey: null }
  for (const plan of plans) {
    if ('placeholderLabel' in plan && plan.placeholderLabel !== undefined) {
      throw new Error(
        `commandsFromPlans: refusing to emit ZPL from an authoring plan (id=${plan.id}). ` +
          `Did you call buildAuthoringPlan instead of buildRenderPlan?`
      )
    }
    switch (plan.kind) {
      case 'text': {
        const xDots = mmToDots(plan.anchorXMm, profile.dpi)
        const yDots = mmToDots(plan.anchorYMm, profile.dpi)
        const fontHeightDots = mmToDots(plan.fontHeightMm, profile.dpi)
        const fontWidthDots = mmToDots(plan.fontWidthMm, profile.dpi)
        // Rotated text ALWAYS pairs with ^FB: with a field block the
        // renderer's `^FO` is the top-left of the rotated block's bounding
        // box (probe-verified, string-independent); WITHOUT one it applies
        // glyph-advance-dependent origin shifts the pipeline cannot model.
        // The rotation-N condition is unchanged so unrotated output stays
        // byte-identical.
        const needsFb = plan.rotation !== 'N' || plan.alignment !== 'L' || plan.maxLines > 1
        // Black-background regions emit a filled `^GB` covering the leaf's
        // box before the text, and toggle `^FR` on the text so the glyphs
        // print white-on-black. Any other value (including `'white'` and
        // `undefined`) emits no extra commands → byte-identical to the
        // pre-Task-14 output for non-black backgrounds.
        const blackBg = plan.regionBackgroundColor === 'black'
        if (blackBg) {
          emitBlackFill(
            commands,
            fillState,
            mmToDots(plan.regionXMm, profile.dpi),
            mmToDots(plan.regionYMm, profile.dpi),
            mmToDots(plan.regionWidthMm, profile.dpi),
            mmToDots(plan.regionHeightMm, profile.dpi)
          )
        }
        if (needsFb) {
          commands.push({
            kind: 'field-block',
            widthDots: mmToDots(plan.wrapBoxWidthMm, profile.dpi),
            maxLines: plan.maxLines,
            lineSpacingDots: mmToDots(plan.lineSpacingMm, profile.dpi),
            alignment: plan.alignment,
            hangingIndentDots: 0,
          })
        }
        commands.push({
          kind: 'text',
          x: xDots,
          y: yDots,
          fontId: plan.fontId,
          fontHeightDots,
          fontWidthDots,
          text: plan.text,
          rotation: plan.rotation,
          ...(blackBg ? { reverseField: true } : {}),
        })
        break
      }
      case 'separator': {
        const widthDots = mmToDots(plan.widthMm, profile.dpi)
        const heightDots = mmToDots(plan.heightMm, profile.dpi)
        const thicknessDots = Math.max(1, mmToDots(plan.thicknessMm, profile.dpi))
        commands.push({
          kind: 'graphic-box',
          x: mmToDots(plan.anchorXMm, profile.dpi),
          y: mmToDots(plan.anchorYMm, profile.dpi),
          widthDots,
          heightDots,
          thicknessDots,
        })
        break
      }
      case 'image': {
        if (plan.buffer === null) break
        commands.push({
          kind: 'image',
          x: mmToDots(plan.anchorXMm, profile.dpi),
          y: mmToDots(plan.anchorYMm, profile.dpi),
          widthDots: mmToDots(plan.widthMm, profile.dpi),
          heightDots: mmToDots(plan.heightMm, profile.dpi),
          data: plan.buffer,
        })
        break
      }
      case 'barcode': {
        // Barcode reverse-field on black backgrounds: ZPL's `^FR` does
        // invert the rendered symbol, but most barcode scanners can't
        // decode an inverted symbol, so this is rarely useful in practice.
        // We still emit the black fill behind the barcode (matching the
        // designer canvas's WYSIWYG behaviour); the reverse-field flag is
        // ON to match — operators who needed light-bars-on-dark would
        // configure their printers to support inverted reads, and
        // operators who don't want this can drop the `style.backgroundColor`.
        const blackBg = plan.regionBackgroundColor === 'black'
        if (blackBg) {
          emitBlackFill(
            commands,
            fillState,
            mmToDots(plan.regionXMm, profile.dpi),
            mmToDots(plan.regionYMm, profile.dpi),
            mmToDots(plan.regionWidthMm, profile.dpi),
            mmToDots(plan.regionHeightMm, profile.dpi)
          )
        }
        // The plan anchors the SYMBOL BOX (quiet zones + bars + HRI band) by
        // its rotated-footprint top-left; `^FO` must point at the rotated BAR
        // field's bounding-box top-left. In the upright frame that is one quiet
        // zone in from the box's left/top edges; under rotation each upright
        // edge faces a different page edge, so the inset permutes. Rotating the
        // upright box 90° CW maps local (u,v) → page (boxH−v, u): the bars'
        // rotated bbox top-left works out to the per-rotation insets below
        // (boxH−quietTop−barsHeight = quietBottom + hriBand). The HRI band is
        // part of the box ALONG the bars' length, so it shifts the bars to the
        // far edge under R/I. Probe-verified against Neodynamic at 203dpi.
        const ql = plan.quietLeftMm ?? 0
        const qr = plan.quietRightMm ?? 0
        const qt = plan.quietTopMm ?? 0
        const qb = plan.quietBottomMm ?? 0
        const hb = plan.hriBandMm ?? 0
        let insetX: number
        let insetY: number
        switch (plan.rotation) {
          case 'R': // 90° CW: bars on the page-right strip, HRI page-left
            insetX = qb + hb
            insetY = ql
            break
          case 'B': // 270° CCW: bars on the page-left strip, HRI page-right
            insetX = qt
            insetY = qr
            break
          case 'I': // 180°: upright left/top edges face page-right/page-bottom
            insetX = qr
            insetY = qb + hb
            break
          default: // 'N'
            insetX = ql
            insetY = qt
        }
        commands.push({
          kind: 'barcode',
          x: mmToDots(plan.anchorXMm + insetX, profile.dpi),
          y: mmToDots(plan.anchorYMm + insetY, profile.dpi),
          symbology: plan.symbology,
          data: plan.data,
          heightDots: plan.heightDots,
          moduleWidthDots: plan.moduleWidthDots,
          humanReadable: plan.humanReadable,
          rotation: plan.rotation,
          ...(blackBg ? { reverseField: true } : {}),
        })
        break
      }
      case 'border-line': {
        emitBorderLine(commands, plan, profile.dpi)
        break
      }
      default: {
        const _ex: never = plan
        throw new Error(`commandsFromPlans: unknown plan ${JSON.stringify(_ex)}`)
      }
    }
  }
  return { commands, warnings: [] }
}

/**
 * Push a single solid-black `^GB` rectangle filling the supplied box. ZPL's
 * `^GB w,h,t` draws a rectangle with border thickness `t`, and enforces a
 * MINIMUM box dimension equal to `t`: if `t > h` the renderer raises the
 * height up to `t` (likewise width if `t > w`). So `t = max(w,h)` would
 * inflate any non-square fill into a `max×max` square on real Zebra hardware
 * and Neodynamic. We use `t = min(w,h)` instead: the border along the longer
 * axis (thickness = the shorter dimension) covers the whole box → a solid
 * fill for any rectangle, and `t ≤ w` and `t ≤ h` so the box can never grow.
 * Clamped to ≥1 dot (matching the separator/border thickness idiom) so a
 * sub-dot region still draws a sliver instead of emitting a no-op. The
 * `kind: 'graphic-box'` command maps to `^FO… ^GBw,h,t^FS`, omitting the
 * colour (default `B`) and rounding (default `0`) — see `serialize.ts`.
 *
 * Dedupe: when the LAST FILL emitted covered this exact box, skip the push.
 * ZPL `^FR` fields XOR against what's already drawn, so re-filling the same
 * black box between two `^FR` fields of one region (e.g. a barcode and its
 * `:hri` text plan, or two localized-rich-text variants) would paint OVER
 * the earlier field's white pixels. Distinct zones always have distinct
 * boxes, so a same-box repeat can only be the same region again — safe to
 * skip regardless of what was emitted in between.
 */
function emitBlackFill(
  commands: ZplCommand[],
  state: { lastFillKey: string | null },
  xDots: number,
  yDots: number,
  widthDots: number,
  heightDots: number
): void {
  const thickness = Math.max(1, Math.min(widthDots, heightDots))
  const key = `${xDots},${yDots},${widthDots},${heightDots}`
  if (state.lastFillKey === key) return
  state.lastFillKey = key
  commands.push({
    kind: 'graphic-box',
    x: xDots,
    y: yDots,
    widthDots,
    heightDots,
    thicknessDots: thickness,
  })
}

/**
 * Translate a `border-line` render plan into one or more `graphic-box`
 * commands. For `style: 'solid'` we emit a single rectangle straddling the
 * cell boundary; for `dashed` / `dotted` we walk the boundary axis emitting
 * short segments at a fixed gap.
 *
 * Dash / dot geometry: `dashed` uses a 2:1 dash-to-gap ratio anchored on
 * the border's thickness; `dotted` uses a 1:1 ratio with square dots.
 * Both are pure functions of the border's thicknessMm and the boundary
 * length, so the output is deterministic for a given (template, profile)
 * tuple.
 */
function emitBorderLine(
  commands: ZplCommand[],
  plan: Extract<RenderPlan, { kind: 'border-line' }>,
  dpi: number
): void {
  const thicknessDots = Math.max(1, mmToDots(plan.thicknessMm, dpi))
  const lengthDots = mmToDots(plan.lengthMm, dpi)
  const xDots = mmToDots(plan.anchorXMm, dpi)
  const yDots = mmToDots(plan.anchorYMm, dpi)

  if (plan.style === 'solid') {
    if (plan.axis === 'vertical') {
      commands.push({
        kind: 'graphic-box',
        x: xDots - Math.floor(thicknessDots / 2),
        y: yDots,
        widthDots: thicknessDots,
        heightDots: lengthDots,
        thicknessDots,
      })
    } else {
      commands.push({
        kind: 'graphic-box',
        x: xDots,
        y: yDots - Math.floor(thicknessDots / 2),
        widthDots: lengthDots,
        heightDots: thicknessDots,
        thicknessDots,
      })
    }
    return
  }

  // Segmented dash / dot. Segment + gap form one period; we keep marching
  // until we exceed the divider length, then truncate the last segment so
  // it does not overshoot the boundary.
  const segmentDots = plan.style === 'dashed' ? Math.max(thicknessDots * 2, 4) : thicknessDots
  const gapDots = plan.style === 'dashed' ? Math.max(thicknessDots, 2) : Math.max(thicknessDots, 2)
  const periodDots = segmentDots + gapDots
  if (periodDots <= 0) return

  for (let offset = 0; offset < lengthDots; offset += periodDots) {
    const segLen = Math.min(segmentDots, lengthDots - offset)
    if (segLen <= 0) break
    if (plan.axis === 'vertical') {
      commands.push({
        kind: 'graphic-box',
        x: xDots - Math.floor(thicknessDots / 2),
        y: yDots + offset,
        widthDots: thicknessDots,
        heightDots: segLen,
        thicknessDots,
      })
    } else {
      commands.push({
        kind: 'graphic-box',
        x: xDots + offset,
        y: yDots - Math.floor(thicknessDots / 2),
        widthDots: segLen,
        heightDots: thicknessDots,
        thicknessDots,
      })
    }
  }
}
